import { useEffect, useRef, useState } from "react";

import {
  PreviewItemRef,
  PreviewResponse,
  useCreateOptimization,
  useOptimization,
  usePreviewOptimization,
  useSendOptimization,
  useUpdateOptimization,
} from "@web-app/api";
import { Button, Header, Icon, useAlert } from "@web-app/components";
import Navigation from "@web-app/pages/common/Navigation";
import { ExternalProviderKind, ProviderExtras } from "@web-app/types";

import { seedFromDraft } from "./draftSeed";
import SaveOptimizationModal from "./modals/SaveOptimizationModal";
import { unquotableItems } from "./providerIssues";
import DeliveryOptionsStep from "./steps/DeliveryOptionsStep";
import ResolveOrderIssuesStep from "./steps/ResolveOrderIssuesStep";
import ResultsStep from "./steps/ResultsStep";
import SelectItemsStep from "./steps/SelectItemsStep";
import SelectProvidersStep from "./steps/SelectProvidersStep";
import { formatCurrency } from "./utils";

type WizardStep = "issues" | "items" | "options" | "providers" | "results";
type ModalMode = "draft" | "send" | null;

const STEP_TITLES: Record<Exclude<WizardStep, "results">, string> = {
  issues: "Orders the providers couldn't quote",
  items: "Select Routes/Point-to-point Orders",
  options: "Delivery Options",
  providers: "Select providers",
};

// Results has no Back: it is reachable from either options or issues, and it
// already offers Edit Providers.
const PREVIOUS_STEP: Partial<Record<WizardStep, WizardStep>> = {
  issues: "options",
  options: "providers",
  providers: "items",
};

const goToLanding = () => {
  window.location.href = "/cpd";
};

type OptimizationWizardProps = {
  draftId?: string;
  logout: () => void;
};

export default function OptimizationWizard({
  draftId,
  logout,
}: OptimizationWizardProps) {
  const { showAlert, showAlertAsync } = useAlert();
  const previewMutation = usePreviewOptimization();
  const createMutation = useCreateOptimization();
  const updateMutation = useUpdateOptimization();
  const sendMutation = useSendOptimization();
  const { data: draft } = useOptimization(draftId ?? null);

  const [step, setStep] = useState<WizardStep>("items");
  const [selectedItems, setSelectedItems] = useState<PreviewItemRef[]>([]);
  const [selectedDspIds, setSelectedDspIds] = useState<string[]>([]);
  const [selectedExternalProviders, setSelectedExternalProviders] = useState<
    ExternalProviderKind[]
  >([]);
  const [providerExtras, setProviderExtras] = useState<ProviderExtras>({});
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<PreviewResponse>();
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const seededRef = useRef(false);

  useEffect(() => {
    if (!draft || seededRef.current) return;
    seededRef.current = true;

    const seeded = seedFromDraft(draft);

    setSelectedItems(seeded.items);
    setSelectedDspIds(seeded.dspWorkspaceIds);
    setSelectedExternalProviders(seeded.externalProviders);
    setProviderExtras(seeded.providerExtras);
    setOverrides(seeded.overrides);
    setPreview({
      avgCostPerDrop: draft.avgCostPerDrop,
      currency: draft.currency,
      dspCount: draft.dspCount,
      items: draft.items,
      totalCost: draft.totalCost,
      totalOrders: draft.totalOrders,
    });
    setStep("results");
  }, [draft]);

  const computePreview = async (
    nextOverrides: Record<string, string>,
    items: PreviewItemRef[] = selectedItems,
    extras: ProviderExtras = providerExtras,
    externals: ExternalProviderKind[] = selectedExternalProviders,
  ) => {
    const result = await previewMutation.mutateAsync({
      dspWorkspaceIds: selectedDspIds,
      externalProviders: externals,
      items,
      overrides: nextOverrides,
      providerExtras: extras,
    });
    setPreview(result);
    return result;
  };

  // Re-quoting hits every provider, so it can take a few seconds with nothing
  // else on screen to show for it.
  const withErrorAlert = async (action: () => Promise<unknown>) => {
    try {
      await showAlertAsync(action(), {
        error: {
          message: "Failed to update optimization. Please try again.",
          title: "Error",
          type: "error",
        },
        loading: { title: "Updating preview ...", type: "default" },
        success: { title: "Preview updated", type: "success" },
      });
    } catch {
      // showAlertAsync has already surfaced the failure.
    }
  };

  const proceedFromProviderSelect = () =>
    withErrorAlert(async () => {
      await computePreview(overrides);
      setStep(selectedExternalProviders.length > 0 ? "options" : "results");
    });

  // The issues page has nothing to say when every order quoted cleanly, so it
  // is skipped rather than shown empty.
  const proceedFromDeliveryOptions = () =>
    withErrorAlert(async () => {
      const result = await computePreview(overrides);
      setStep(unquotableItems(result.items).length > 0 ? "issues" : "results");
    });

  const handleInternalDspChange = (dspWorkspaceIds: string[]) => {
    setSelectedDspIds(dspWorkspaceIds);
    setOverrides((prev) => {
      const allowed = new Set(dspWorkspaceIds);
      return Object.fromEntries(
        Object.entries(prev).filter(([, value]) => {
          if (value.startsWith("external:")) return true;
          return allowed.has(value);
        }),
      );
    });
  };

  const handleExternalProvidersChange = (kinds: ExternalProviderKind[]) => {
    setSelectedExternalProviders(kinds);
    setOverrides((prev) => {
      const allowedExternals = new Set(kinds.map((k) => `external:${k}`));
      return Object.fromEntries(
        Object.entries(prev).filter(([, value]) => {
          if (value.startsWith("external:")) return allowedExternals.has(value);
          return true;
        }),
      );
    });
  };

  const handleOverride = (itemId: string, ref: string) => {
    const next = { ...overrides, [itemId]: ref };
    setOverrides(next);
    return withErrorAlert(() => computePreview(next));
  };

  const handleReset = () => {
    setOverrides({});
    return withErrorAlert(() => computePreview({}));
  };

  const handleBulkMove = (itemIds: string[], ref: string) => {
    const next = { ...overrides };
    itemIds.forEach((id) => {
      next[id] = ref;
    });
    setOverrides(next);
    return withErrorAlert(() => computePreview(next));
  };

  const handleBulkDelete = (itemIds: string[]) => {
    const removeSet = new Set(itemIds);
    const remaining = selectedItems.filter(
      (item) => !removeSet.has(item.itemId),
    );
    const next = { ...overrides };
    itemIds.forEach((id) => delete next[id]);
    setSelectedItems(remaining);
    setOverrides(next);
    return withErrorAlert(() => computePreview(next, remaining));
  };

  const handleSave = async (name: string) => {
    try {
      const payload = {
        dspWorkspaceIds: selectedDspIds,
        externalProviders: selectedExternalProviders,
        items: selectedItems,
        name,
        overrides,
        providerExtras,
      };
      const saved = draftId
        ? await updateMutation.mutateAsync({ id: draftId, payload })
        : await createMutation.mutateAsync(payload);
      if (modalMode === "send") {
        await sendMutation.mutateAsync(saved.id);
      }
      goToLanding();
    } catch {
      showAlert({
        message: "Failed to save optimization. Please try again.",
        title: "Error",
        type: "error",
      });
    }
  };

  const isSent = draft?.status === "SENT";
  const hasSelection =
    selectedDspIds.length > 0 || selectedExternalProviders.length > 0;
  const previousStep = PREVIOUS_STEP[step];

  const footer = (
    <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-white px-6 py-3">
      <div className="text-sm text-gray-500">
        {selectedItems.length} selected
      </div>
      <div className="flex gap-2">
        <Button color="light" onClick={goToLanding}>
          {isSent ? "Close" : "Cancel"}
        </Button>
        {previousStep && (
          <Button color="light" onClick={() => setStep(previousStep)}>
            Back
          </Button>
        )}
        {step === "items" && (
          <Button
            onClick={() => setStep("providers")}
            disabled={selectedItems.length === 0}
          >
            Proceed
          </Button>
        )}
        {step === "providers" && (
          <Button
            onClick={proceedFromProviderSelect}
            disabled={!hasSelection}
            isLoading={previewMutation.isPending}
          >
            Proceed
          </Button>
        )}
        {step === "options" && (
          <Button
            onClick={proceedFromDeliveryOptions}
            isLoading={previewMutation.isPending}
          >
            Proceed
          </Button>
        )}
        {step === "issues" && (
          <Button
            onClick={() => setStep("results")}
            isLoading={previewMutation.isPending}
          >
            Proceed
          </Button>
        )}
        {step === "results" && !isSent && (
          <>
            <Button color="light" onClick={() => setModalMode("draft")}>
              Save
            </Button>
            <Button onClick={() => setModalMode("send")}>Save and send</Button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-screen min-h-screen max-w-full">
      <Navigation logout={logout} />

      <div className="flex h-screen flex-col lg:pl-20">
        <Header
          icon="truck"
          title="Cost Optimizer"
          description="Compare providers and optimize delivery costs"
        />

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {step === "results" ? (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <Button color="light" onClick={() => setStep("providers")}>
                  Edit Providers
                </Button>
                <span className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700">
                  <Icon name="boxesPacking" className="h-3 w-3 text-gray-400" />
                  {selectedItems.length} Routes Added
                </span>
                <Button
                  color="light"
                  onClick={handleReset}
                  disabled={Object.keys(overrides).length === 0}
                >
                  Reset
                </Button>
              </div>
              {preview && (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                  <span>
                    <span className="text-gray-500">Total Cost - </span>
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(
                        preview.totalCost,
                        preview.currency || "USD",
                      )}
                    </span>
                  </span>
                  <span>
                    <span className="text-gray-500">
                      Average Cost Per Drop{" "}
                    </span>
                    <span className="font-semibold text-gray-900">
                      (
                      {formatCurrency(
                        preview.avgCostPerDrop,
                        preview.currency || "USD",
                      )}
                      )
                    </span>
                  </span>
                  <span>
                    <span className="text-gray-500">Total Drops - </span>
                    <span className="font-semibold text-gray-900">
                      {preview.totalOrders}
                    </span>
                  </span>
                  <span>
                    <span className="text-gray-500">Assigned #DSP - </span>
                    <span className="font-semibold text-gray-900">
                      {preview.dspCount}
                    </span>
                  </span>
                </div>
              )}
            </div>
          ) : (
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              {STEP_TITLES[step]}
            </h2>
          )}

          {step === "items" && (
            <SelectItemsStep
              selectedItems={selectedItems}
              onChange={setSelectedItems}
            />
          )}
          {step === "providers" && (
            <SelectProvidersStep
              selectedDspIds={selectedDspIds}
              selectedExternalProviders={selectedExternalProviders}
              onInternalChange={handleInternalDspChange}
              onExternalChange={handleExternalProvidersChange}
            />
          )}
          {step === "options" && (
            <DeliveryOptionsStep
              extras={providerExtras}
              onChange={setProviderExtras}
              previewItems={preview?.items ?? []}
              providerKinds={selectedExternalProviders}
            />
          )}
          {step === "issues" && (
            <ResolveOrderIssuesStep
              onOrderEdited={() =>
                withErrorAlert(() => computePreview(overrides))
              }
              previewItems={preview?.items ?? []}
            />
          )}
          {step === "results" && (
            <ResultsStep
              preview={preview}
              isLoading={previewMutation.isPending}
              onOverride={handleOverride}
              onBulkMove={handleBulkMove}
              onBulkDelete={handleBulkDelete}
            />
          )}
        </div>

        {footer}
      </div>

      <SaveOptimizationModal
        isOpen={modalMode !== null}
        mode={modalMode === "send" ? "send" : "draft"}
        initialName={draft?.name || ""}
        isSubmitting={
          createMutation.isPending ||
          updateMutation.isPending ||
          sendMutation.isPending
        }
        onClose={() => setModalMode(null)}
        onSubmit={handleSave}
      />
    </div>
  );
}
